import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";

interface Promotions {
  id: number;
  title: string;
  image: string;
  link: string;
  start_date: string;
  end_date: string;
  priority: number;
  status: string;
}

export default function PromotionBanners() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateOnly } = useFormat();

  const [data, setData] = useState<Promotions[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);
  const formatDateForInput = (date: string) => (date ? date.split("T")[0] : "");

  /* =========================
     FORM FIELDS
  ========================= */

  const bannerFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },

    { key: "title", label: "Title", colSpan: 2 },

    {
      key: "image",
      label: "Promotion Banner",
      type: "image",
      imageType: "single",
      imageFolder: "promotions",
      uploadField: "promotionBanner",
      colSpan: 2,
    },

    { key: "link", label: "Banner Link", colSpan: 2 },

    {
      key: "start_date",
      label: "Start Date",
      type: "date",
      colSpan: 2,
    },

    {
      key: "end_date",
      label: "End Date",
      type: "date",
      colSpan: 2,
    },

    {
      key: "priority",
      label: "Priority",
      type: "number",
      colSpan: 2,
    },

    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  const columns = [
    { key: "title", label: "Title" },
    { key: "image", label: "Image", imageFolder: "promotions" },
    {
      key: "start_date",
      label: "Start Date",
      type: "date",
      colSpan: 2,
    },

    {
      key: "end_date",
      label: "End Date",
      type: "date",
      colSpan: 2,
    },
    { key: "priority", label: "Priority" },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  /* =========================
     FETCH BANNERS
  ========================= */

  const fetchBanners = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/settings/banners`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Banners Error:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  /* =========================
     ADD / UPDATE
  ========================= */

  const buildFormData = (
    formData: any,
    fields: FormField[],
    activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      /* =========================
       PRODUCT IMAGES (MULTIPLE)
    ========================= */

      if (field.type === "image" && field.imageType === "product") {
        if (Array.isArray(value)) {
          value.forEach((img: any, index: number) => {
            // Upload new image
            if (img?.file) {
              form.append(field.uploadField || "image", img.file);
            }

            // Remove existing image
            if (!img && activeRow?.images?.[index]?.id) {
              form.append("removeImages[]", activeRow.images[index].id);
            }
          });
        }
      } else if (field.type === "image") {
        /* =========================
       SINGLE IMAGE
    ========================= */
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed) {
          form.append(field.uploadField || field.key, "REMOVE");
        }
      } else {
        /* =========================
       NORMAL FIELDS
    ========================= */
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, bannerFields, activeRow);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/settings/banners`
          : `${BASE_URL}/admin/settings/banners/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchBanners();
        setModalOpen(false);
        setActiveRow(null);
      }
    } catch (error) {
      console.error("Submit Error:", error);
    }
  };

  /* =========================
     DELETE (SOFT DELETE)
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Banner",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/settings/banners/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchBanners();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Banner deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Promotion Banners"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");

          const formattedRow = {
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");

          const formattedRow = {
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: Promotions) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.title}</h3>

                {item.image && (
                  <img
                    src={`${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/promotions/${item.image}`}
                    alt={item.title}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "10px",
                    }}
                  />
                )}
                <p>Start : {formatDateOnly(item.start_date)}</p>

                <p>End : {formatDateOnly(item.end_date)}</p>

                <p>
                  <strong>Priority:</strong> {item.priority}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status?.charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Promotion Banner"
        fields={bannerFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
